import React, { useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import Menu from './_menu/component';
import Link from '../../base_link/component';
import { Icon } from 'audamatic-ui';
import { getDevice, isHtmlLink } from '@utils';
import { useEventListener } from '@utils/hooks';

import './styles/styles.scss';

const Navigation = ({
  items = [],
  showMobileMenu,
  toggleMobileMenu,
  additionalLinks = [],
}) => {
  const [menuTop, setMenuTop] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const navInnerRef = useRef(null);
  const history = useHistory();
  const mainItems = items.filter((item) => !item.excluded);
  const excludedItems = items.filter((item) => !!item.excluded);
  const { isMobile, isTablet } = getDevice();

  // Close the mobile menu on resize to prevent unintended layouts
  const handleResize = () => {
    if (showMobileMenu) {
      closeMobileMenu();
    }
  };
  useEventListener('resize', handleResize);

  const getItemByUrl = (url) => {
    return items.find((item) => item.url === url) || {};
  };

  /**
   * Toggle the mega menu on desktop and the secondary panel on mobile
   * by setting a selected enu item, whcih contains the sub components.
   * Triggered on hover and by handleItemClick
   */
  const toggleMenuItem = (url, element = null) => {
    if (!url) {
      setSelectedItem(null);
      setMenuTop(null);
      return;
    }
    const item = getItemByUrl(url);
    if (item.components?.length) {
      if (element && navInnerRef.current) {
        const navRect = navInnerRef.current.getBoundingClientRect();
        const itemRect = element.getBoundingClientRect();
        const top = itemRect.bottom - navRect.top;
        setMenuTop(top);
      }
      setSelectedItem(item);
    } else {
      setSelectedItem(null);
      setMenuTop(null);
    }
  };

  /**
   * Navigate back in mobile menu (subcategories -> categories)
   */
  const navigateBack = () => {
    setShowSubmenu(false);
  };

  const closeMobileMenu = () => {
    toggleMobileMenu({ forceClose: true });
    setShowSubmenu(false);
    toggleMenuItem(null);
  };

  const navigateTo = (url) => {
    if (!url) return;
    if (isHtmlLink(url)) {
      window.location.href = url;
    } else {
      history.push(url);
    }
  };

  /**
   * Determine which action to take when clicking on a menu item.
   * If it doesn't have any sections (components), then simply navigate to url.
   * Otherwise show mega menu or secondary panel on mobile menu.
   */
  const handleItemClick = (url, element = null) => {
    const item = getItemByUrl(url);

    if (!item) return;

    if (isMobile || isTablet) {
      if (item.components?.length) {
        setSelectedItem(item);
        setShowSubmenu(true);
      } else {
        navigateTo(item.url || '/');
        closeMobileMenu();
      }
      return;
    }

    if (item.components?.length) {
      toggleMenuItem(url, element);
    } else {
      toggleMenuItem(null);
      navigateTo(item.url || '/');
    }
  };

  const renderMobileMenuHeader = ({ title, nested = false, tuckable }) => {
    return (
      <div
        className={`mobile-menu-header tuckable ${nested ? 'nested' : ''}  ${
          tuckable && showSubmenu ? 'tucked-away' : ''
        }`}
      >
        {title}
        <div className="close-menu menu-header-icon" onClick={closeMobileMenu}>
          <Icon name="Xmark" size="md" />
        </div>
        {nested && (
          <div className="nav-back menu-header-icon" onClick={navigateBack}>
            <Icon name="NavArrowLeft" size="md" />
          </div>
        )}
      </div>
    );
  };

  const renderItem = (item) => {
    const { highlighted, excluded, title, url = '', components = [] } = item;
    const hasMenu = !!components.length;
    const active = selectedItem?.url === url;
    const classNames = [
      highlighted && 'highlighted',
      excluded && 'excluded',
      hasMenu && 'has-menu',
      active && 'active',
    ]
      .filter(Boolean)
      .join(' ');
    const element = url.split('/')[1];

    if (!url) {
      return null;
    }

    return (
      <li
        key={url}
        className={classNames}
        onMouseEnter={(e) => toggleMenuItem(url, e.currentTarget)}
        onClick={() => handleItemClick(url)}
        data-element={element}
      >
        <Link to={url}>
          {title}
          {hasMenu && (
            <div className="menu-caret">
              {isMobile || isTablet ? (
                <Icon name="NavArrowRight" size="sm" />
              ) : (
                <Icon name="NavArrowDown" size="xs" />
              )}
            </div>
          )}
          <div />
        </Link>
      </li>
    );
  };

  return (
    <>
      <div
        className={`mobile-backdrop ${showMobileMenu ? 'visible' : ''}`}
        onClick={closeMobileMenu}
      />
      <div className={`main-navigation ${showMobileMenu ? 'visible' : ''}`}>
        {showMobileMenu &&
          renderMobileMenuHeader({ title: 'Menu', tuckable: true })}
        <nav
          ref={navInnerRef}
          className="container"
          onMouseLeave={() => {
            if (!showMobileMenu) {
              toggleMenuItem(null);
            }
          }}
        >
          <div className={`tuckable ${showSubmenu ? 'tucked-away' : ''}`}>
            <ul
              className="main-nav-items"
              data-test-id="component-mainNavigation"
            >
              {mainItems.map((item, index) => renderItem(item, index))}
            </ul>
            <ul className="excluded-nav-items">
              {excludedItems.map((item, index) => renderItem(item, index))}
              {showMobileMenu &&
                additionalLinks.map((link) => (
                  <li
                    key={link.url}
                    className="excluded-mobile-item"
                    onClick={closeMobileMenu}
                  >
                    <Link to={link.url}>{link.title}</Link>
                  </li>
                ))}
            </ul>
          </div>
          <div
            className={`nav-menu ${selectedItem ? 'active' : ''} ${
              showSubmenu ? 'visible' : ''
            }`}
            style={menuTop !== null ? { top: `${menuTop}px` } : undefined}
          >
            {showMobileMenu &&
              renderMobileMenuHeader({
                title: selectedItem?.title,
                nested: true,
              })}
            <div className="container">
              {mainItems.map((item, index) => {
                const isActive =
                  selectedItem &&
                  selectedItem['component-id'] === item['component-id'];
                return (
                  <Menu
                    key={index}
                    item={item}
                    toggle={toggleMenuItem}
                    closeMobileMenu={closeMobileMenu}
                    show={isActive}
                  />
                );
              })}
            </div>
          </div>
        </nav>
      </div>
    </>
  );
};

Navigation.propTypes = {
  items: PropTypes.array,
  toggleMobileMenu: PropTypes.func,
  showMobileMenu: PropTypes.bool,
  additionalLinks: PropTypes.array,
};

export default Navigation;
